import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import type { Icon as PhosphorIcon, IconWeight } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import {
  ICON_FOR_TILE,
  iconRecipe,
  iconTileRecipe,
  type IconRecipeProps,
  type IconTileSize,
} from '../recipes/icon.recipe';

/**
 * An icon passed as the component itself: a lucide-react one, as the
 * dashboard draws, or a Phosphor one, as the mobile app's Figma draws
 * (HouseLine, PhoneCall, Wall). Either takes a className for its size, which
 * is all Icon gives it.
 */
export type IconComponent = LucideIcon | PhosphorIcon;

export interface IconProps
  extends Omit<React.SVGAttributes<SVGSVGElement>, 'color'>,
    IconRecipeProps {
  /** The icon component itself: `icon={Truck}` or `icon={HouseLine}`. */
  icon: IconComponent;
  /**
   * What a screen reader should announce. Omit for icons that only decorate
   * text already on screen — they are then correctly hidden from assistive
   * technology.
   */
  label?: string;
  /**
   * A hover tooltip, drawn as the SVG's own `<title>`. An SVG ignores a
   * `title` attribute, so this is how an icon that stands for a state (a
   * verdict tick, a warning mark) says what it means on hover.
   */
  title?: string;
  /**
   * A Phosphor icon's style: `fill` for the solid glyphs the mobile Figma
   * draws (the presence pill's person), `regular` otherwise. A lucide icon
   * has one style and ignores it.
   */
  weight?: IconWeight;
}

/**
 * Renders an icon at a size from the token scale.
 *
 * Using this instead of `<Truck className="h-4 w-4" />` means icon sizing is
 * adjustable from tokens.css, and that the decorative-vs-meaningful
 * accessibility decision is made explicitly at every call site.
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  {
    icon: IconComponent,
    size,
    tone,
    spin,
    filled,
    label,
    title,
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <IconComponent
      ref={ref}
      className={cn(iconRecipe({ size, tone, spin, filled }), className)}
      strokeWidth="var(--icon-stroke-width)"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </IconComponent>
  );
});

export interface IconTileProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'>,
    Pick<IconProps, 'icon' | 'label'> {
  /** Tile size as its step on the 4px grid, the same scale `gap` uses. */
  size?: IconTileSize;
  shape?: 'square' | 'circle';
  /** The tile's background. `none` leaves it untinted. */
  tone?:
    | 'none'
    | 'neutral'
    | 'primary'
    | 'success'
    | 'warning'
    | 'info'
    | 'destructive'
    | 'active';
  /** Override the glyph size. Defaults to one that suits the tile. */
  iconSize?: IconProps['size'];
  /** The glyph's colour, which is not always the tile's. */
  iconTone?: IconProps['tone'];
}

/**
 * An icon inside a tinted tile. For empty states, feature lists, integration
 * cards and notification rows, where a bare icon reads as too small and too
 * quiet.
 *
 *   <IconTile icon={Phone} size={10} tone="info" iconTone="success" />
 *
 * `tone` is the tile; `iconTone` is the glyph. They are separate because the
 * product uses them separately — an info-blue tile with a green tick is how an
 * integration card says "connected".
 */
export const IconTile = React.forwardRef<HTMLSpanElement, IconTileProps>(
  function IconTile(
    {
      icon,
      label,
      size = 10,
      shape,
      tone,
      iconSize,
      iconTone,
      className,
      ...props
    },
    ref
  ) {
    return (
      <span
        ref={ref}
        className={cn(iconTileRecipe({ size, shape, tone }), className)}
        {...props}
      >
        <Icon
          icon={icon}
          label={label}
          size={iconSize ?? ICON_FOR_TILE[size]}
          tone={iconTone}
        />
      </span>
    );
  }
);
